import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Card,
  CardContent,
  Container,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  Box,
  Tab,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { TabPanel } from "@mui/lab";
import { CloudUpload, Description, Folder } from "@mui/icons-material";
import HeaderHelpPage from "../../components/HeaderHelpPage";
import TabsMenu from "../../components/TabsMenu";
import { styles } from "../../utils/styles";
import { sendDataWithPost } from "../../services/services";
import { showSuccessMsg } from "../../utils/toast";
import ConfirmDialog from "../../components/ConfirmDialog";
import CircularLoader from "../../components/circularLoader";
import { acquirerList, PAYMENT_HOST_LIST } from "../../utils/constants";

const excludedAcquirerIds = [PAYMENT_HOST_LIST.FIUU, PAYMENT_HOST_LIST.LIQUIDNET];

// Master List File Upload Component
const MasterListFileUpload = ({ selectedAcquirer, onAcquirerChange, onAcquirerSelect }) => {
  const fileInputRef = useRef(null);
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const handleUploadFile = () => {
    setOpenConfirmDialog(false);
    setLoading(true);
    const formData = new FormData();

    formData.append("file", data);
    formData.append("acquirer", selectedAcquirer.value);
    const url = `/payout/ingestor/upload_master_file/`;

    sendDataWithPost(
      url,
      formData,
      null,
      () => setLoading(false),
      (res) => {
        showSuccessMsg(res.data.detail);
        setData();
        fileInputRef.current.value = null;
        setOpenConfirmDialog(false);
        // Trigger refresh of master list files
        window.dispatchEvent(new CustomEvent('refreshMasterListFiles', { 
          detail: { acquirer: selectedAcquirer.value } 
        }));
      },
      null,
      {
        "Content-Type": "multipart/form-data",
      }
    );
  };

  const handleChange = (e) => {
    if (e.target.files.length) {
      setData(e.target.files[0]);
    } else {
      setData();
    }
  };

  const handleAcquirerChange = (e, newValue) => {
    if (newValue) {
      const acquirerData = {
        value: newValue.id,
        label: newValue.label,
      };
      onAcquirerChange(acquirerData);
      // Trigger file fetch for master list files
      onAcquirerSelect(acquirerData.value, 'masterlist');
    }
  };

  return (
    <Card elevation={3} sx={{ height: 'fit-content' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CloudUpload sx={{ mr: 1, color: 'secondary.main' }} />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Upload Master List File
          </Typography>
        </Box>
        
        <FormControl fullWidth sx={{ mb: 3 }}>
          <Autocomplete
            options={acquirerList
            .filter((item) => !excludedAcquirerIds.includes(item.id))
            .map((item) => ({
              id: item.id,
              label: item.name,
            }))}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Folder sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                  {option.label}
                </Box>
              </li>
            )}
            onChange={handleAcquirerChange}
            value={selectedAcquirer}
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Acquirer"
                variant="outlined"
                fullWidth
              />
            )}
            isOptionEqualToValue={(option) =>
              option.id === selectedAcquirer.value
            }
          />
        </FormControl>

        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              border: '2px dashed',
              borderColor: data ? 'success.main' : 'grey.300',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              backgroundColor: data ? 'success.light' : 'grey.50',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                borderColor: data ? 'success.dark' : 'secondary.main',
                backgroundColor: data ? 'success.light' : 'secondary.50',
              }
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv, .xlsx, .xls"
              onChange={handleChange}
              style={{ display: 'none' }}
            />
            
            {data ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  backgroundColor: 'success.main',
                  mb: 2
                }}>
                  <Description sx={{ color: 'white', fontSize: 30 }} />
                </Box>
                <Typography variant="h6" color="success.main" sx={{ fontWeight: 600, mb: 1 }}>
                  File Selected Successfully!
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {data.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(data.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Click to change file
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  backgroundColor: 'secondary.main',
                  mb: 2
                }}>
                  <CloudUpload sx={{ color: 'white', fontSize: 30 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Choose a file
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Supported formats: XLSX, XLS
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  backgroundColor: 'secondary.main',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}>
                  <CloudUpload sx={{ fontSize: 18 }} />
                  Browse Files
                </Box>
              </Box>
            )}
          </Box>
          
          {/* File validation info */}
          {data && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              borderRadius: 1, 
              backgroundColor: 'success.light',
              border: '1px solid',
              borderColor: 'success.main'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: 'success.main',
                  mr: 1
                }} />
                <Typography variant="body2" color="success.dark" sx={{ fontWeight: 500 }}>
                  File validation passed
                </Typography>
              </Box>
              <Typography variant="caption" color="success.dark">
                File type and size are valid for upload
              </Typography>
            </Box>
          )}
        </Box>

        <LoadingButton
          size="large"
          variant="contained"
          fullWidth
          sx={{ 
            ...styles.customButton, 
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            bgcolor: 'secondary.main',
            '&:hover': {
              bgcolor: 'secondary.dark',
            }
          }}
          onClick={() =>
            data && selectedAcquirer.value && setOpenConfirmDialog(true)
          }
          disabled={!data || !selectedAcquirer.value}
          loading={loading}
          startIcon={<CloudUpload />}
        >
          Upload Master List File
        </LoadingButton>
      </CardContent>

      {openConfirmDialog && (
        <ConfirmDialog
          open={true}
          maxWidth="md"
          title={`Are you sure you want to upload the master list file '${data?.name}' to '${selectedAcquirer.label}'?`}
          onClose={() => setOpenConfirmDialog(false)}
          onSubmit={() => handleUploadFile()}
        />
      )}
    </Card>
  );
};

// Master List Files Component
const MasterListFiles = ({ selectedAcquirer, isActive }) => {
  const [masterListFilesUploaded, setMasterListFilesUploaded] = useState([]);
  const [isFetchingMasterListFiles, setIsFetchingMasterListFiles] = useState(false);
  const [masterListPage, setMasterListPage] = useState(0);
  const [rowsPerPage] = useState(10);

  const handleGetMasterListFilesUploaded = (acquirerId) => {
    if (!acquirerId) return;
    
    setIsFetchingMasterListFiles(true);
    const url = "/payout/ingestor/master_files_uploaded/";
    sendDataWithPost(
      url, 
      { acquirer: acquirerId }, 
      setMasterListFilesUploaded, 
      () => setIsFetchingMasterListFiles(false)
    );
  };

  // Listen for master list file upload events to refresh the list
  useEffect(() => {
    const handleRefreshMasterListFiles = (event) => {
      const { acquirer } = event.detail;
      handleGetMasterListFilesUploaded(acquirer);
    };

    window.addEventListener('refreshMasterListFiles', handleRefreshMasterListFiles);
    
    return () => {
      window.removeEventListener('refreshMasterListFiles', handleRefreshMasterListFiles);
    };
  }, []);

  // Only fetch files when this component is active and acquirer is selected
  useEffect(() => {
    if (isActive && selectedAcquirer.value) {
      handleGetMasterListFilesUploaded(selectedAcquirer.value);
      // Reset pagination when acquirer changes
      setMasterListPage(0);
    }
  }, [isActive, selectedAcquirer.value]);

  const renderFilesListContent = (files, type, currentPage, setCurrentPage) => {
    if (files.length > 0) {
      // Calculate pagination
      const startIndex = currentPage * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      const paginatedFiles = files.slice(startIndex, endIndex);
      const totalPages = Math.ceil(files.length / rowsPerPage);

      const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
      };

      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {startIndex + 1}-{Math.min(endIndex, files.length)} of {files.length} {type.toLowerCase()} file{files.length > 1 ? 's' : ''}
          </Typography>
          
          <Paper elevation={1} sx={{ maxHeight: 400, overflow: 'auto' }}>
            <List dense>
              {paginatedFiles.map((item, index) => (
                <React.Fragment key={index}>
                  <ListItem sx={{ 
                    py: 2,
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Description sx={{ mr: 2, color: 'secondary.main' }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.FileName}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          <Chip 
                            label={item.Size} 
                            size="small" 
                            variant="outlined" 
                            color="secondary"
                          />
                          <Chip 
                            label={item.LastModified} 
                            size="small" 
                            variant="outlined" 
                            color="primary"
                          />
                        </Box>
                      </Box>
                    </Box>
                  </ListItem>
                  {index < paginatedFiles.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              mt: 2,
              gap: 1
            }}>
              <LoadingButton
                size="small"
                onClick={(e) => handlePageChange(e, currentPage - 1)}
                disabled={currentPage === 0}
                sx={{ minWidth: 40 }}
              >
                ←
              </LoadingButton>
              
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {Array.from({ length: totalPages }, (_, index) => {
                  // Show first page, last page, current page, and pages around current
                  const shouldShow = 
                    index === 0 || 
                    index === totalPages - 1 || 
                    (index >= currentPage - 1 && index <= currentPage + 1);
                  
                  if (!shouldShow) {
                    // Show ellipsis if there's a gap
                    if (index === currentPage - 2 || index === currentPage + 2) {
                      return (
                        <Typography key={index} sx={{ px: 1, color: 'text.secondary' }}>
                          ...
                        </Typography>
                      );
                    }
                    return null;
                  }

                  return (
                    <LoadingButton
                      key={index}
                      size="small"
                      variant={currentPage === index ? "contained" : "outlined"}
                      onClick={(e) => handlePageChange(e, index)}
                      sx={{ 
                        minWidth: 32, 
                        height: 32,
                        fontSize: '0.875rem'
                      }}
                    >
                      {index + 1}
                    </LoadingButton>
                  );
                })}
              </Box>
              
              <LoadingButton
                size="small"
                onClick={(e) => handlePageChange(e, currentPage + 1)}
                disabled={currentPage === totalPages - 1}
                sx={{ minWidth: 40 }}
              >
                →
              </LoadingButton>
            </Box>
          )}
        </Box>
      );
    }
    
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        py: 6,
        color: 'text.secondary'
      }}>
        <Folder sx={{ fontSize: 60, mb: 2, opacity: 0.5 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>
          No master list files
        </Typography>
        <Typography variant="body2">
          Upload master list files to see them listed here
        </Typography>
      </Box>
    );
  };

  if (isFetchingMasterListFiles) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularLoader size={40}/>
      </Box>
    );
  }

  return renderFilesListContent(
    masterListFilesUploaded?.result || [], 
    "Master List", 
    masterListPage, 
    setMasterListPage
  );
};

const UploadAcquirerFile = () => {
  const [selectedTab, setSelectedTab] = useState("Master List");
  const [selectedAcquirer, setSelectedAcquirer] = useState({ value: "", label: "" });

  const handleTabChange = (event, value) => {
    setSelectedTab(value);
    // Clear selectedAcquirer when switching tabs
    setSelectedAcquirer({ value: "", label: "" });
  };

  const handleAcquirerChange = (acquirerData) => {
    setSelectedAcquirer(acquirerData);
  };

  const handleAcquirerSelect = (acquirerId, fileType) => {
    // This function will be called when an acquirer is selected
    // The file fetching will be handled by the respective file list components
    // based on which tab is active
  };

  return (
    <Container maxWidth="xl">
      <HeaderHelpPage title="Import Data" />

      <Grid container spacing={3}>
        <Grid item md={5} sm={12} xs={12}>
          <TabsMenu value={selectedTab} onChange={handleTabChange}>
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip 
                    label="Master" 
                    size="small" 
                    color="secondary" 
                    sx={{ mr: 1 }}
                  />
                  Master List Files
                </Box>
              } 
              value="Master List" 
            />
            
            <TabPanel value="Master List">
              <MasterListFileUpload 
                selectedAcquirer={selectedAcquirer}
                onAcquirerChange={handleAcquirerChange}
                onAcquirerSelect={handleAcquirerSelect}
              />
            </TabPanel>
          </TabsMenu>
        </Grid>

        <Grid item md={7} sm={12} xs={12}>
          <Card elevation={3} sx={{ height: 'fit-content' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Folder sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                  Master List Files Uploaded
                </Typography>
              </Box>
              <MasterListFiles 
                selectedAcquirer={selectedAcquirer}
                isActive={selectedTab === "Master List"}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default UploadAcquirerFile;
